import type { Resume } from "@adafia/types";
import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	type SensorDescriptor,
	type SensorOptions,
} from "@dnd-kit/core";
import {
	restrictToFirstScrollableAncestor,
	restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Briefcase } from "lucide-react";
import { InputGroup } from "./InputGroup";
import { SectionHeader } from "./SectionHeader";
import { SortableItem } from "./SortableItem";
import { TextAreaGroup } from "./TextAreaGroup";

interface ExperienceEditorProps {
	experience: Resume["experience"];
	onAdd: () => void;
	onUpdate: (id: string, field: string, value: string) => void;
	onRemove: (id: string) => void;
	onDragEnd: (event: DragEndEvent) => void;
	sensors: SensorDescriptor<SensorOptions>[];
}

export function ExperienceEditor({
	experience,
	onAdd,
	onUpdate,
	onRemove,
	onDragEnd,
	sensors,
}: ExperienceEditorProps) {
	return (
		<>
			<SectionHeader title="Experience" icon={Briefcase} onAdd={onAdd} />
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={onDragEnd}
				modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
			>
				<SortableContext
					items={experience.map((e) => e.id)}
					strategy={verticalListSortingStrategy}
				>
					{experience.map((exp) => (
						<SortableItem
							key={exp.id}
							id={exp.id}
							onDelete={() => onRemove(exp.id)}
						>
							<InputGroup
								label="Role"
								value={exp.role}
								onChange={(v) => onUpdate(exp.id, "role", v)}
							/>
							<InputGroup
								label="Company"
								value={exp.company}
								onChange={(v) => onUpdate(exp.id, "company", v)}
							/>
							<div className="form-grid">
								<InputGroup
									label="Start Date"
									type="month"
									value={exp.startDate || ""}
									onChange={(v) => onUpdate(exp.id, "startDate", v)}
								/>
								<InputGroup
									label="End Date"
									type="month"
									value={exp.endDate === "Present" ? "" : exp.endDate || ""}
									onChange={(v) => onUpdate(exp.id, "endDate", v)}
									disabled={exp.endDate === "Present"}
								/>
							</div>
							<div className="form-group" style={{ marginBottom: "1rem" }}>
								<label
									className="form-label"
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.5rem",
										cursor: "pointer",
									}}
								>
									<input
										type="checkbox"
										checked={exp.endDate === "Present"}
										onChange={(e) =>
											onUpdate(
												exp.id,
												"endDate",
												e.target.checked ? "Present" : "",
											)
										}
									/>
									I currently work here
								</label>
							</div>
							<InputGroup
								label="Location"
								value={exp.location}
								onChange={(v) => onUpdate(exp.id, "location", v)}
							/>
							<TextAreaGroup
								label="Description"
								value={exp.description}
								onChange={(v) => onUpdate(exp.id, "description", v)}
							/>
						</SortableItem>
					))}
				</SortableContext>
			</DndContext>
		</>
	);
}
